import React, { useMemo, useState, useEffect } from 'react';
import { LineChart } from '../../components/charts';

export default function CategorySeasonality() {
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [termToMonthly, setTermToMonthly] = useState({}); // { term: [{year, month, monthly_searches}] }
  const DEMO_BRAND_ID_MI = '2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a';
  const currentBrandId = typeof window !== 'undefined' ? (localStorage.getItem('currentBrandId') || null) : null;
  const buildSeasonSeriesForTerm = (base = 12000, mult = 1) => {
    const yearsToFill = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];
    const arr = [];
    for (const y of yearsToFill) {
      for (let m = 1; m <= 12; m++) {
        const seasonFactor =
          (m === 5 || m === 6) ? 1.25 :
          (m === 9 || m === 10 || m === 11) ? 1.35 :
          (m === 12 || m === 1) ? 1.15 :
          (m === 7 || m === 8) ? 0.9 :
          1.0;
        const noise = 0.9 + (Math.sin((y + m) * 13.7) + 1) * 0.05;
        const monthly = Math.round(base * seasonFactor * noise * mult);
        arr.push({ year: y, month: m, monthly_searches: monthly });
      }
    }
    return arr;
  };

  const currentYear = new Date().getFullYear();
  useEffect(() => {
    setExpanded(prev => ({ ...prev, [String(currentYear)]: true }));
  }, [currentYear]);

  // Load brand categories from monthly-category-summary (ALL time) and preselect first few
  useEffect(() => {
    if (currentBrandId === DEMO_BRAND_ID_MI) return;
    const loadBrandCategories = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const url = `https://api.glowytics.com/api/google-ads/monthly-category-summary?dateRange=all`;
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (!res.ok) return;
        const js = await res.json();
        const rows = (js && Array.isArray(js.data)) ? js.data : [];
        const names = Array.from(new Set(rows.map(r => (r.itemCategory || '').trim()).filter(n => n && n.toLowerCase() !== 'unknown' && n !== '(not set)')));
        if (names.length > 0) {
          setCategories(names);
          const initialSel = names.slice(0, 3);
          setSelectedCategories(initialSel);
          // Prefetch volumes for initially selected categories
          initialSel.forEach((c) => fetchTermMonthly(c));
        }
      } catch (e) {
        console.warn('[CategorySeasonality] Failed to load brand categories', e);
      }
    };
    loadBrandCategories();
  }, []);

  // DEMO: Inject additional dummy categories/brands/keywords and volumes (keeps incoming API data)
  useEffect(() => {
    if (currentBrandId !== DEMO_BRAND_ID_MI) return;
    // Define demo terms
    const demoCategories = [
      'Kadın Spor > Yürüyüş',
      'Kadın Spor > Koşu',
      'Erkek Spor > Koşu',
      'Çocuk Spor > Sneakers',
      'Anne & Bebek > Bebek Arabası',
      'Spor > Outdoor > Hiking',
      'Outdoor > Trekking Botu',
      'Elektronik > Spor Saat',
      'Kışlık > Mont',
      'Yazlık > Sandalet'
    ];
    const demoBrands = ['Skechers','Nike','Adidas','Puma','New Balance','Asics','Hummel'];
    const demoKeywords = [
      'skechers',
      "skechers d'lites",
      'skechers go run',
      'skechers yürüyüş ayakkabısı',
      'kadın spor ayakkabı',
      'erkek koşu ayakkabısı',
      'hiking botu',
      'bebek arabası',
      'spor saat'
    ];
    // Merge into lists (avoid duplicates)
    setCategories(prev => Array.from(new Set([...(prev || []), ...demoCategories])));
    setBrands(prev => Array.from(new Set([...(prev || []), ...demoBrands])));
    setKeywords(prev => Array.from(new Set([...(prev || []), ...demoKeywords])));
    // Ensure some are selected by default
    setSelectedCategories(prev => {
      const add = demoCategories.slice(0, 4).filter(x => !prev.includes(x));
      return [...prev, ...add];
    });
    setSelectedBrands(prev => {
      const add = demoBrands.slice(0, 3).filter(x => !prev.includes(x));
      return [...prev, ...add];
    });
    setSelectedKeywords(prev => {
      const add = demoKeywords.slice(0, 3).filter(x => !prev.includes(x));
      return [...prev, ...add];
    });
    // Build seasonal monthly volumes for last 3 years
    const yearsToFill = [new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2];
    const buildSeasonSeries = (base, mult = 1) => {
      const arr = [];
      for (const y of yearsToFill) {
        for (let m = 1; m <= 12; m++) {
          // seasonal bump by month
          const seasonFactor =
            (m === 5 || m === 6) ? 1.25 :
            (m === 9 || m === 10 || m === 11) ? 1.35 :
            (m === 12 || m === 1) ? 1.15 :
            (m === 7 || m === 8) ? 0.9 :
            1.0;
          const noise = 0.9 + (Math.sin((y + m) * 13.7) + 1) * 0.05; // slight variation
          const monthly = Math.round(base * seasonFactor * noise * mult);
          arr.push({ year: y, month: m, monthly_searches: monthly });
        }
      }
      return arr;
    };
    const injectForTerms = (terms, base, mult = 1) => {
      setTermToMonthly(prev => {
        const next = { ...prev };
        for (const t of terms) {
          if (!next[t]) next[t] = buildSeasonSeries(base, mult);
        }
        return next;
      });
    };
    // Inject with different intensities
    injectForTerms(demoCategories, 12000, 1.0);
    injectForTerms(demoBrands, 9000, 0.85);
    injectForTerms(demoKeywords, 14000, 1.1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBrandId]);

  // Load top keywords from keyword-full-performance (last90) and preselect a few
  useEffect(() => {
    if (currentBrandId === DEMO_BRAND_ID_MI) return;
    const loadTopKeywords = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const url = `https://api.glowytics.com/api/google-ads/keyword-full-performance?dateRange=last90`;
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (!res.ok) return;
        const rows = await res.json();
        const map = {};
        (rows || []).forEach((r) => {
          const k = r.keyword_text;
          if (!k || k === '(not set)' || k === '--') return;
          if (!map[k]) map[k] = { keyword: k, cost: 0 };
          map[k].cost += Number(r.cost || 0);
        });
        const top = Object.values(map).sort((a,b)=> b.cost - a.cost).slice(0, 30).map(x => x.keyword);
        setKeywords(top);
        const init = top.slice(0, 2);
        setSelectedKeywords(init);
        init.forEach((k) => fetchTermMonthly(k));
      } catch (e) {
        console.warn('[CategorySeasonality] Failed to load top keywords', e);
      }
    };
    loadTopKeywords();
  }, []);

  // Load brand list from monthly-item-brand-summary (ALL time) and preselect a few
  useEffect(() => {
    if (currentBrandId === DEMO_BRAND_ID_MI) return;
    const loadBrands = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        const url = `https://api.glowytics.com/api/google-ads/monthly-item-brand-summary?dateRange=all`;
        const res = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include'
        });
        if (!res.ok) return;
        const js = await res.json();
        const rows = (js && Array.isArray(js.data)) ? js.data : [];
        const names = Array.from(new Set(rows.map(r => (r.itemBrand || '').trim()).filter(Boolean)));
        if (names.length > 0) {
          setBrands(names);
          const initialSel = names.slice(0, 2);
          setSelectedBrands(initialSel);
          initialSel.forEach((b) => fetchTermMonthly(b));
        }
      } catch (e) {
        console.warn('[CategorySeasonality] Failed to load brands', e);
      }
    };
    loadBrands();
  }, []);

  const fetchTermMonthly = async (term) => {
    try {
      if (currentBrandId === DEMO_BRAND_ID_MI) {
        setTermToMonthly(prev => {
          if (Array.isArray(prev[term])) return prev;
          const next = { ...prev };
          const t = String(term || '').toLowerCase();
          const base =
            t.includes('skechers') ? 16000 :
            t.includes('kadın') || t.includes('women') ? 13000 :
            t.includes('erkek') || t.includes('men') ? 12000 :
            t.includes('çocuk') || t.includes('cocuk') || t.includes('kids') ? 9000 :
            t.includes('outdoor') || t.includes('hiking') ? 11000 :
            10000;
          next[term] = buildSeasonSeriesForTerm(base, 1.0);
          return next;
        });
        return;
      }
      const brandId = localStorage.getItem('currentBrandId');
      const res = await fetch('https://api.glowytics.com/api/search-volume/monthly', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) },
        // Use Turkish language by default for TR-focused seasonality.
        // Backend normalizes numeric/resource-name formats.
        body: JSON.stringify({ keyword: term, months: 0, languageConstant: '1037' })
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        let msg = 'Failed to fetch volume';
        if (ct.includes('application/json')) {
          const js = await res.json().catch(() => ({}));
          msg = js.error || js.message || js.details?.error || msg;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      const monthly = Array.isArray(data.monthly) ? data.monthly : [];
      setTermToMonthly(prev => ({ ...prev, [term]: monthly }));
    } catch (e) {
      console.error('Fetch monthly error:', term, e);
      setTermToMonthly(prev => ({ ...prev, [term]: [] }));
    }
  };

  const handleAddCategory = (name) => {
    if (!name) return;
    if (!categories.includes(name)) {
      setCategories(prev => [...prev, name]);
      fetchTermMonthly(name);
    }
    if (!selectedCategories.includes(name)) setSelectedCategories(prev => [...prev, name]);
    setIsAddOpen(false);
    setSearchText('');
  };

  const getMonthlyValue = (term, year, monthIndex) => {
    const arr = termToMonthly[term];
    if (!Array.isArray(arr)) return 0;
    const row = arr.find(r => Number(r.year) === year && Number(r.month) === (monthIndex+1));
    return row ? Number(row.monthly_searches || 0) : 0;
  };

  const years = []; for (let y = currentYear; y >= 2022; y--) years.push(y);
  const dataByYear = {};
  years.forEach((y) => {
    const allItems = [...categories, ...brands, ...keywords];
    dataByYear[y] = monthNames.map((name, idx) => ({ name, idx, volumes: Object.fromEntries(allItems.map(c => [c, getMonthlyValue(c, y, idx)])) }));
  });

  // Last 12 months chart dataset
  const last12Data = (() => {
    const rows = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(); d.setMonth(d.getMonth() - i);
      const y = d.getFullYear(); const mIdx = d.getMonth();
      const label = `${monthNames[mIdx].slice(0,3)} ${y}`;
      const row = { label };
      const selectedAll = [...selectedCategories, ...selectedBrands, ...selectedKeywords];
      selectedAll.forEach((c) => { row[c] = getMonthlyValue(c, y, mIdx); });
      rows.push(row);
    }
    return rows;
  })();

  const palette = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#22c55e'];
  const paletteFill = ['rgba(37,99,235,0.12)','rgba(22,163,74,0.12)','rgba(245,158,11,0.12)','rgba(239,68,68,0.12)','rgba(139,92,246,0.12)','rgba(6,182,212,0.12)','rgba(249,115,22,0.12)','rgba(34,197,94,0.12)'];
  const chartJsData = {
    labels: last12Data.map(r => r.label),
    datasets: [...selectedCategories, ...selectedBrands, ...selectedKeywords].map((c, i) => ({
      label: c,
      data: last12Data.map(r => Number(r[c] || 0)),
      borderColor: palette[i % palette.length],
      backgroundColor: paletteFill[i % paletteFill.length],
      fill: true,
      pointRadius: 0,
      borderWidth: 2,
      tension: 0.35
    }))
  };
  const chartJsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: true } },
    scales: {
      x: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: '#f1f5f9' } },
      y: { ticks: { color: '#64748b', font: { size: 10 } }, grid: { color: '#f1f5f9' } }
    }
  };

  const toggleCategory = (c) => {
    setSelectedCategories(prev => {
      const next = prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c];
      if (!prev.includes(c) && !Array.isArray(termToMonthly[c])) {
        fetchTermMonthly(c);
      }
      return next;
    });
  };

  const toggleBrand = (b) => {
    setSelectedBrands(prev => {
      const next = prev.includes(b) ? prev.filter(x => x !== b) : [...prev, b];
      if (!prev.includes(b) && !Array.isArray(termToMonthly[b])) {
        fetchTermMonthly(b);
      }
      return next;
    });
  };
  const toggleKeyword = (k) => {
    setSelectedKeywords(prev => {
      const next = prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k];
      if (!prev.includes(k) && !Array.isArray(termToMonthly[k])) {
        fetchTermMonthly(k);
      }
      return next;
    });
  };
  const upToCurrentMonth = new Date().getMonth();
  const format = (n) => Number(n).toLocaleString('tr-TR');
  const yearTotal = (year, cat) => {
    const endIdx = year === currentYear ? upToCurrentMonth : 11;
    let sum = 0; for (let i=0;i<=endIdx;i++) sum += dataByYear[year][i].volumes[cat] || 0; return sum;
  };

  // Compute expected lift windows (top 3 months by average across all years)
  const expectedLift = (cat) => {
    const arr = [];
    years.forEach((y) => { for (let m=0;m<12;m++){ const v = getMonthlyValue(cat, y, m); if (!Number.isFinite(v)) continue; arr.push({ m, v }); } });
    const agg = Array.from({ length: 12 }, (_, m) => ({ m, v: arr.filter(a => a.m===m).reduce((s,x)=>s+x.v,0) }));
    const top = agg.sort((a,b)=>b.v-a.v).slice(0,3).map(x=>x.m).sort((a,b)=>a-b);
    return top;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/star.svg" alt="Category Seasonality" className="w-14 h-14 object-contain" />
            <div>
              <h1 className="text-2xl tracking-tight">Category Seasonality</h1>
              <p className="text-sm text-gray-500">Google search volume by category. Last 12 months chart; yearly breakdown below.</p>
            </div>
          </div>
          <div>
            <button className="px-3 py-1.5 rounded-lg border border-dashed hover:border-gray-400 text-sm" onClick={() => setIsAddOpen(true)}>+ Add category</button>
          </div>
        </div>

        {/* Category chips */}
        <div className="p-4 border-b bg-white space-y-3">
          <div>
            <div className="mb-1 text-xs text-gray-500">Categories</div>
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map(c => (
                <button key={c} className={`px-3 py-1.5 rounded-full text-xs border transition ${selectedCategories.includes(c) ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`} onClick={() => toggleCategory(c)}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-gray-500">Brands</div>
            <div className="flex items-center gap-2 flex-wrap">
              {brands.map(b => (
                <button key={b} className={`px-3 py-1.5 rounded-full text-xs border transition ${selectedBrands.includes(b) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`} onClick={() => toggleBrand(b)}>{b}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 text-xs text-gray-500">Top Keywords (last 90d)</div>
            <div className="flex items-center gap-2 flex-wrap">
              {keywords.map(k => (
                <button key={k} className={`px-3 py-1.5 rounded-full text-xs border transition ${selectedKeywords.includes(k) ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`} onClick={() => toggleKeyword(k)}>{k}</button>
              ))}
            </div>
          </div>
        </div>

        {[...selectedCategories, ...selectedBrands, ...selectedKeywords].length > 0 ? (
          <>
            {/* Line chart last 12 months */}
            <div className="px-6 py-4 bg-white">
              <div className="mb-2 text-sm text-gray-600">Last 12 months</div>
              <div className="h-72">
                <LineChart data={chartJsData} options={chartJsOptions} />
              </div>
            </div>

            {/* Yearly table */}
            <div className="px-6 py-4 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '24%' }}>Period</th>
                      {[...selectedCategories, ...selectedBrands, ...selectedKeywords].map(c => (
                        <th key={c} className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {years.map((yearLoop) => {
                      const key = `${yearLoop}`; const isOpen = !!expanded[key];
                      const toggle = () => setExpanded(prev => ({ ...prev, [key]: !isOpen }));
                      return (
                        <React.Fragment key={key}>
                          <tr className="bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 cursor-pointer" onClick={toggle}>
                              <span className="mr-2">{isOpen ? '▾' : '▸'}</span>{yearLoop}
                            </td>
                            {[...selectedCategories, ...selectedBrands, ...selectedKeywords].map(c => (
                              <td key={`${key}-tot-${c}`} className="px-4 py-3 text-sm text-right font-semibold">{format(yearTotal(yearLoop, c))}</td>
                            ))}
                          </tr>
                          {isOpen && (
                            (yearLoop === currentYear
                              ? dataByYear[yearLoop].filter(m => m.idx <= upToCurrentMonth).slice().reverse()
                              : dataByYear[yearLoop].slice().reverse()
                            ).map((m) => (
                              <tr key={`${key}-m-${m.idx}`}>
                                <td className="px-4 py-3 text-sm text-gray-700 pl-8">{monthNames[m.idx]}</td>
                              {[...selectedCategories, ...selectedBrands, ...selectedKeywords].map(c => (
                                  <td key={`${key}-${c}-m-${m.idx}`} className="px-4 py-3 text-sm text-right">{format(m.volumes[c] || 0)}</td>
                                ))}
                              </tr>
                            ))
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Lift windows */}
            <div className="px-6 py-4 bg-white border-t">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[...selectedCategories, ...selectedBrands, ...selectedKeywords].map(cat => (
                  <div key={cat} className="rounded-xl border p-4">
                    <div className="text-sm font-semibold text-gray-900 mb-2">{cat} • Expected Lift Window</div>
                    <div className="flex gap-2 flex-wrap">
                      {expectedLift(cat).map(idx => (
                        <span key={idx} className="px-2 py-1 rounded-md text-xs bg-blue-50 text-blue-700 border border-blue-100">{monthNames[idx].slice(0,3)}</span>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 mt-2">Suggestion: Plan pushes in these months.</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="px-6 py-16 bg-white">
            <div className="flex flex-col items-center justify-center text-center gap-3">
              <img src="/trend.svg" alt="Seasonality" className="w-24 h-24" />
              <h3 className="text-lg font-medium text-gray-800">Add categories to view seasonality</h3>
              <p className="text-sm text-gray-500 max-w-md">We’ll fetch Google Ads monthly search volumes per category and show last 12 months and yearly breakdowns.</p>
              <button className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" onClick={() => setIsAddOpen(true)}>+ Add category</button>
            </div>
          </div>
        )}
      </div>

      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsAddOpen(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-lg mx-4">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Category</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsAddOpen(false)}>✕</button>
            </div>
            <div className="p-5 space-y-4">
              <input
                autoFocus
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Type category (e.g., Sneakers, Winter Coats)"
              />
              <div className="flex justify-end">
                <button className="px-3 py-2 rounded-lg bg-black text-white text-sm" onClick={() => handleAddCategory(searchText.trim())} disabled={!searchText.trim()}>Add “{searchText.trim() || ''}”</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
